import { useEffect, MouseEvent, useState } from 'react';
import { SerializedError } from '@reduxjs/toolkit';
import { useForm } from 'react-hook-form';

import { useAppDispatch } from '../../app/hooks';
import {
  getScopeAction,
  UpdateScopeActionResponse,
  updateScopeAction,
  scope_field_options,
} from './scopeSlice';
import { Scope } from '../../types';

type Props = {
  tenant_id: string;
  scope_name: string;
  successCallback: (result: UpdateScopeActionResponse) => void;
  dismissCallback: (error?: SerializedError) => void;
};

const initialState: Required<Scope> = {
  scope_id: '',
  name: '',
  title: '',
  description: '',
};

export function ScopeEditForm({ tenant_id, scope_name, successCallback, dismissCallback }: Props) {
  const dispatch = useAppDispatch();
  const [api_error, setApiError] = useState<SerializedError|undefined>();
  const { handleSubmit, register, watch, reset, formState: { errors } } = useForm({
    defaultValues: initialState,
  });

  useEffect(() => {
    (async() => {
      let result = await dispatch(getScopeAction({ tenant_id, scope_name }));
      if (getScopeAction.fulfilled.match(result)) {
        reset({
          ...initialState,
          ...result.payload,
        });
      } else {
        const error = result.error;
        setApiError(error);
        dismissCallback(error);
      }
    })();
  }, [tenant_id, scope_name, dispatch, dismissCallback, reset]);
  const handleUpdate = async(data: Scope) => {
    let result = await dispatch(updateScopeAction({
      tenant_id,
      scope_name: data.name,
      body: data,
    }));
    if (updateScopeAction.fulfilled.match(result)) {
      successCallback(result.payload);
    } else {
      setApiError(result.error);
      const subscription = watch(() => {
        setApiError(undefined);
        subscription.unsubscribe();
      });
    }
  };
  const handleCancel = (event: MouseEvent) => {
    event.preventDefault();
    dismissCallback();
  };
  return (
    <form onSubmit={handleSubmit(handleUpdate)}>
      <fieldset>
        <div className="mb-3 row">
          <label htmlFor="scope_name" className="col-sm-3 col-form-label">Name</label>
          <div className="col-sm-6">
            <input disabled type="text" id="scope_name" className="form-control-plaintext"
              { ...register('name') }
            />
          </div>
        </div>
        <div className="mb-3 row">
          <label htmlFor="scope_scope_id" className="col-sm-3 col-form-label">ScopeId</label>
          <div className="col-sm-6">
            <input disabled type="text" id="scope_scope_id" className="form-control-plaintext"
              { ...register('scope_id') }
            />
          </div>
        </div>
        <div className="mb-3 row">
          <label htmlFor="scope_title" className="col-sm-3 col-form-label">Title</label>
          <div className="col-sm-6">
            <input id="scope_title" className="form-control"
              { ...register('title', scope_field_options.title) }
            />
          </div>
          <span className="col-sm-3 text-danger py-1">{errors.title?.message}</span>
        </div>
        <div className="mb-3 row">
          <label htmlFor="scope_description" className="col-sm-3 col-form-label">Description</label>
          <div className="col-sm-6">
            <textarea id="scope_description" className="form-control"
              { ...register('description', scope_field_options.description) }
            >
            </textarea>
          </div>
          <span className="col-sm-3 text-danger py-1">{errors.description?.message}</span>
        </div>
        <div className="d-flex">
          <span>{api_error?.message ?? api_error?.name}</span>
        </div>
        <div className="d-flex my-3 justify-content-center">
          <button type="submit" className="btn btn-primary mx-3">Update</button>
          <button className="btn btn-light mx-1" onClick={(e) => handleCancel(e)}>Cancel</button>
        </div>
      </fieldset>
    </form>
  );
}
